import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import { getServerUrl } from '../config/runtimeConfig';

// Application Insights service for React frontend
class AppInsightsService {
  constructor() {
    this.appInsights = null;
    this.initialized = false;
    this.reactPlugin = null;
    this.browserHistory = null;
  }

  // Initialize Application Insights with the provided configuration
  initialize(configValue) {
    if (this.initialized) {
      console.log('App Insights already initialized');
      return true;
    }

    if (!configValue) {
      console.error('No configuration value provided');
      return false;
    }    try {      // Create React plugin first
      this.reactPlugin = new ReactPlugin();

      // Detect corporate proxy environment early
      const isCorporateProxy = this.isCorporateProxyEnvironment();
      const isDevelopment = process.env.NODE_ENV === 'development';
      console.log('🔧 Application Insights initialization - Corporate proxy detected:', isCorporateProxy, 'Development mode:', isDevelopment);

      // Configure App Insights with proxy-aware settings
      const appConfig = {
        enableCorsCorrelation: !isCorporateProxy && !isDevelopment, // Disable correlation in proxy environments and dev
        enableRequestHeaderTracking: !isCorporateProxy && !isDevelopment, // Disable in proxy environments and dev
        enableResponseHeaderTracking: !isCorporateProxy && !isDevelopment, // Disable in proxy environments and dev
        disableExceptionTracking: false,
        disableFetchTracking: isCorporateProxy || isDevelopment, // Disable fetch tracking in proxy environments and dev
        disableAjaxTracking: isCorporateProxy || isDevelopment, // Disable AJAX tracking in proxy environments and dev
        enableAutoRouteTracking: !isDevelopment, // Disable auto route tracking in dev
        maxBatchInterval: 10000, // Increase batch interval to reduce frequency
        isBeaconApiDisabled: true,
        useBeacon: false,
        enableDebug: false, // Disable debug mode to reduce noise
        loggingLevelConsole: isDevelopment ? 0 : 2, // Disable console logging in dev
        maxAjaxCallsPerView: 2000, // Further increase Ajax call limit
        disableDataLossAnalysis: true, // Disable data loss analysis
        enablePerfMgr: false, // Disable performance manager
        disableInstrumentationKeyValidation: true, // Skip validation to reduce calls
        extensions: [this.reactPlugin],        extensionConfig: {
          [this.reactPlugin.identifier]: {
            historyType: 'browser'
          }
        }
      };
      
      if (isCorporateProxy) {
        console.log('🔍 Corporate proxy detected - disabling Application Insights correlation and request tracking');
      }

      // Add connection string or key
      const isConnectionString = configValue.includes('InstrumentationKey=');
      if (isConnectionString) {
        appConfig.connectionString = configValue.trim();
        const ikMatch = configValue.match(/InstrumentationKey=([^;]+)/);
        if (ikMatch?.[1]) {
          appConfig.instrumentationKey = ikMatch[1].trim();
        }
      } else {
        appConfig.instrumentationKey = configValue.trim();
      }

      // Initialize App Insights
      this.appInsights = new ApplicationInsights({ config: appConfig });
      this.appInsights.loadAppInsights();

      if (!this.appInsights.core) {
        throw new Error('Failed to initialize - core not available');
      }

      // Add telemetry initializer to mark all telemetry as coming from frontend
      this.appInsights.addTelemetryInitializer((envelope) => {
        envelope.data = envelope.data || {};
        envelope.data.source = 'frontend';
        envelope.data.component = 'react-web';
        
        // Filter out noisy Application Insights internal errors
        if (envelope.baseType === 'ExceptionData') {
          const exceptionData = envelope.data?.baseData;
          const message = exceptionData?.exceptions?.[0]?.message || '';
          
          // Suppress Ajax monitoring errors
          if (message.includes('Failed to monitor XMLHttpRequest') ||
              message.includes('Failed to calculate the duration of the ajax call') ||
              message.includes('Maximum ajax per page view limit reached') ||
              message.includes('AI (Internal)')) {
            console.debug('🔇 Suppressing Application Insights internal error:', message);
            return false; // Don't send this telemetry
          }
        }
        
        return true;
      });

      this.initialized = true;

      // Set up features
      this.trackTrace('Application Insights initialized Frontend');
      this.trackPageView();
      this.setupFetchTracking();
      this._addDebugListeners();

      // Set up proxy if needed
      const backendUrl = getServerUrl();
      if (backendUrl?.toLowerCase().startsWith('http')) {
        try {
          const { patchAppInsightsEndpoint } = require('./appInsightsProxy');
          const isProxied = patchAppInsightsEndpoint(this.appInsights);
          if (isProxied) {
            this.trackTrace('Proxy enabled', 1, { backendUrl });
          }
        } catch (e) {
          console.warn('Failed to set up proxy:', e);
        }
      }

      return true;
    } catch (error) {
      console.error('Failed to initialize:', error);
      this.initialized = false;
      return false;
    }
  }

  // Initialize development mode (logs to console only)
  initializeForDevelopment() {
    if (this.initialized) return true;

    try {
      this.appInsights = {
        trackEvent: (e) => console.log('📊 [DEV] Event:', e),
        trackTrace: (t) => console.log('🔍 [DEV] Trace:', t),
        trackException: (e) => console.log('❌ [DEV] Exception:', e),
        trackPageView: (p) => console.log('📄 [DEV] PageView:', p),
        trackMetric: (m) => console.log('📏 [DEV] Metric:', m),
        flush: () => console.log('🔄 [DEV] Flush'),
        context: { 
          telemetryTrace: { 
            traceID: `dev-${Date.now()}`,
            parentID: '00000000'
          }
        },
        core: { 
          getTransmissionControls: () => [] 
        }
      };

      this.initialized = true;
      return true;
    } catch (error) {
      console.error('Failed to initialize dev mode:', error);
      return false;
    }
  }

  // Track event
  trackEvent(name, properties = {}) {
    if (!this.initialized || !this.appInsights) return;
    try {
      this.appInsights.trackEvent({ name, properties });
    } catch (e) {
      console.error('Error tracking event:', e);
    }
  }

  // Track exception
  trackException(exception, properties = {}) {
    if (!this.initialized || !this.appInsights) return;
    try {
      this.appInsights.trackException({
        exception,
        properties: {
          ...properties,
          stack: exception.stack,
          message: exception.message
        }
      });
    } catch (e) {
      console.error('Error tracking exception:', e);
    }
  }

  // Track trace
  trackTrace(message, severityLevel = 1, properties = {}) {
    if (!this.initialized || !this.appInsights) return;
    try {
      this.appInsights.trackTrace({ 
        message, 
        severityLevel, 
        properties 
      });
    } catch (e) {
      console.error('Error tracking trace:', e);
    }
  }

  // Track page view
  trackPageView(name, url, properties = {}) {
    if (!this.initialized || !this.appInsights) return;
    try {
      this.appInsights.trackPageView({
        name: name || document.title,
        uri: url || window.location.href,
        properties
      });
    } catch (e) {
      console.error('Error tracking page view:', e);
    }
  }

  // Track metric
  trackMetric(name, value, properties = {}) {
    if (!this.initialized || !this.appInsights) return;
    try {
      this.appInsights.trackMetric({ 
        name, 
        average: value, 
        properties 
      });
    } catch (e) {
      console.error('Error tracking metric:', e);
    }
  }

  // Handle React error boundary errors
  handleError(error, errorInfo) {
    if (!error) return;
    this.trackException(error, {
      errorInfo: errorInfo ? JSON.stringify(errorInfo) : undefined
    });
  }

  // Get React plugin
  getReactPlugin() {
    return this.reactPlugin;
  }
  // Get logger instance
  getLogger(prefix = '') {
    return {
      info: (message, properties = {}) => {
        // Always log to console for debugging
        console.log(`[${prefix}] INFO:`, message, properties);
        
        // Send to App Insights if initialized
        if (this.initialized && this.appInsights) {
          this.appInsights.trackTrace({
            message: `[${prefix}] ${message}`,
            severityLevel: 1,
            properties: { ...properties, prefix, logLevel: 'info' }
          });
        }
      },
      warn: (message, properties = {}) => {
        // Always log to console for debugging
        console.warn(`[${prefix}] WARN:`, message, properties);
        
        // Send to App Insights if initialized
        if (this.initialized && this.appInsights) {
          this.appInsights.trackTrace({
            message: `[${prefix}] ${message}`,
            severityLevel: 2,
            properties: { ...properties, prefix, logLevel: 'warn' }
          });
        }
      },
      error: (message, properties = {}) => {
        // Always log to console for debugging
        console.error(`[${prefix}] ERROR:`, message, properties);
        
        // Send to App Insights if initialized
        if (this.initialized && this.appInsights) {
          this.appInsights.trackTrace({
            message: `[${prefix}] ${message}`,
            severityLevel: 3,
            properties: { ...properties, prefix, logLevel: 'error' }
          });
        }
      },
      verbose: (message, properties = {}) => {
        // Always log to console for debugging
        console.debug(`[${prefix}] VERBOSE:`, message, properties);
        
        // Send to App Insights if initialized
        if (this.initialized && this.appInsights) {
          this.appInsights.trackTrace({
            message: `[${prefix}] ${message}`,
            severityLevel: 0,
            properties: { ...properties, prefix, logLevel: 'verbose' }
          });
        }
      }
    };
  }  // Get component-specific logger with standardized tagging
  getComponentLogger(component, operation = null) {
    const standardTags = {
      source: 'frontend',
      component: component,
      version: process.env.REACT_APP_VERSION || '1.0.0',
      environment: process.env.NODE_ENV || 'development',
      userAgent: navigator.userAgent.substring(0, 100),
      url: window.location.href,
      timestamp: new Date().toISOString()
    };
    
    if (operation) {
      standardTags.operation = operation;
    }
    
    return {
      info: (message, properties = {}) => {
        const mergedProperties = { ...standardTags, ...properties };
        
        // Always log to console for debugging
        console.log(`[${component}] INFO:`, message, mergedProperties);
        
        // Send to App Insights if initialized
        if (this.initialized && this.appInsights) {
          this.appInsights.trackTrace({
            message: `[${component}] ${message}`,
            severityLevel: 1,
            properties: { ...mergedProperties, logLevel: 'info' }
          });
        }
      },
      warn: (message, properties = {}) => {
        const mergedProperties = { ...standardTags, ...properties };
        
        // Always log to console for debugging
        console.warn(`[${component}] WARN:`, message, mergedProperties);
        
        // Send to App Insights if initialized
        if (this.initialized && this.appInsights) {
          this.appInsights.trackTrace({
            message: `[${component}] ${message}`,
            severityLevel: 2,
            properties: { ...mergedProperties, logLevel: 'warn' }
          });
        }
      },
      error: (message, properties = {}) => {
        const mergedProperties = { ...standardTags, ...properties };
        
        // Always log to console for debugging
        console.error(`[${component}] ERROR:`, message, mergedProperties);
        
        // Send to App Insights if initialized
        if (this.initialized && this.appInsights) {
          this.appInsights.trackTrace({
            message: `[${component}] ${message}`,
            severityLevel: 3,
            properties: { ...mergedProperties, logLevel: 'error' }
          });
        }
      },
      verbose: (message, properties = {}) => {
        const mergedProperties = { ...standardTags, ...properties };
        
        // Always log verbose in console for debugging (changed from development-only)
        console.debug(`[${component}] VERBOSE:`, message, mergedProperties);
        
        // Send to App Insights if initialized
        if (this.initialized && this.appInsights) {
          this.appInsights.trackTrace({
            message: `[${component}] ${message}`,
            severityLevel: 0,
            properties: { ...mergedProperties, logLevel: 'verbose' }
          });
        }
      },
      trackEvent: (name, properties = {}) => {
        const mergedProperties = { ...standardTags, ...properties };
        
        if (this.initialized && this.appInsights) {
          this.appInsights.trackEvent({
            name: `${component}:${name}`,
            properties: mergedProperties
          });
        }
      },
      trackMetric: (name, value, properties = {}) => {
        const mergedProperties = { ...standardTags, ...properties };
        
        if (this.initialized && this.appInsights) {
          this.appInsights.trackMetric({
            name: `${component}:${name}`,
            average: value,
            properties: mergedProperties
          });
        }
      },
      trackException: (exception, properties = {}) => {
        const mergedProperties = { ...standardTags, ...properties };
        
        if (this.initialized && this.appInsights) {
          this.appInsights.trackException({
            exception,
            properties: {
              ...mergedProperties,
              stack: exception.stack,
              message: exception.message
            }
          });
        }
      }
    };
  }  // Helper method to detect corporate proxy environment
  isCorporateProxyEnvironment() {
    try {
      // Check if the current page is served through Ericom proxy
      const currentlyThroughProxy = window.location.href.includes('shield.ericomcloud.net') ||
                                   window.location.origin.includes('ericomcloud.net') ||
                                   navigator.userAgent.includes('Ericom');

      // Enhanced detection: also check if we have any indication of corporate proxy usage
      const hasProxyIndicators = document.referrer.includes('ericomcloud.net');

      const isProxy = currentlyThroughProxy || hasProxyIndicators;
        if (isProxy) {
        console.log('🔍 Corporate proxy environment detected with details:', {
          currentlyThroughProxy,
          hasProxyIndicators,
          hostname: window.location.hostname,
          userAgent: navigator.userAgent.substring(0, 100),
          environment: process.env.NODE_ENV
        });
      }
      
      return isProxy;
    } catch {
      return false;
    }
  }

  // Set up fetch tracking with correlation headers
  setupFetchTracking() {
    if (!this.initialized || !this.appInsights) {
      console.warn('⚠️ Application Insights not initialized, skipping fetch tracking setup');
      return;
    }
    
    try {
      console.log('🔧 Setting up Application Insights fetch tracking...');
      const originalFetch = window.fetch;
      const isCorporateProxy = this.isCorporateProxyEnvironment();
      
      if (isCorporateProxy) {
        console.log('🔍 Corporate proxy environment detected - using proxy-friendly configuration');
      }
      
      window.fetch = async (input, init = {}) => {
        // IMMEDIATE BYPASS: Check for Google Photos API first, before any processing
        const url = typeof input === 'string' ? input : input?.url;
        // Skip tracking for Google Photo Picker API requests
        if (url && (url.includes('photoslibrary.googleapis.com') || url.includes('googleapis.com/js/api.js') || url.includes('picker'))) {
          // Return original fetch directly without any Application Insights involvement
          return originalFetch(input, init);
        }

        init.headers = init.headers || {};

        // Add correlation headers for our backend (skip in corporate proxy environments)
        const isOwnBackend = url && (
          url.includes('/api/') || 
          url.includes('localhost') || 
          url.startsWith('/') ||
          (!url.startsWith('http') || url.includes(window.location.hostname))
        );        // Skip correlation headers for file uploads in corporate proxy environments
        const isFileUpload = init.body instanceof FormData;
        const isMediaUpload = url && (url.includes('/media-files') || url.includes('/media') || url.includes('upload'));
        const isSoundFontFile = url && (url.includes('.js') && (url.includes('soundfont') || url.includes('acoustic_grand_piano') || url.includes('/public/')));
        
        const skipCorrelationHeaders = (isCorporateProxy && (isFileUpload || isMediaUpload)) || isSoundFontFile;
        
        // AGGRESSIVE FIX: Completely disable Application Insights for file uploads in proxy environments or SoundFont files
        if (skipCorrelationHeaders) {
          console.log('🔍 Skipping Application Insights correlation - details:', {
            url,
            isFileUpload,
            isMediaUpload,
            isSoundFontFile,
            isCorporateProxy,
            reason: isSoundFontFile ? 'SoundFont file detected' : 'Corporate proxy with file/media upload'
          });
          
          // Remove all possible correlation headers that might be added by Application Insights
          const headersToRemove = [
            'Request-Id', 'request-id', 'RequestId',
            'Request-Context', 'request-context', 'RequestContext',
            'traceparent', 'Traceparent', 'TraceParent',
            'tracestate', 'Tracestate', 'TraceState',
            'x-correlation-id', 'X-Correlation-Id', 'X-CORRELATION-ID',
            'x-request-id', 'X-Request-Id', 'X-REQUEST-ID',
            'x-ms-request-id', 'X-MS-Request-Id', 'X-MS-REQUEST-ID',
            'x-ms-request-root-id', 'X-MS-Request-Root-Id', 'X-MS-REQUEST-ROOT-ID',
            'x-ms-client-request-id', 'X-MS-Client-Request-Id', 'X-MS-CLIENT-REQUEST-ID',
            'x-ms-correlation-request-id', 'X-MS-Correlation-Request-Id', 'X-MS-CORRELATION-REQUEST-ID',
            'x-ms-session-id', 'X-MS-Session-Id', 'X-MS-SESSION-ID',
            'correlation-context', 'Correlation-Context', 'CORRELATION-CONTEXT'
          ];
          
          // Remove headers from different header formats
          if (init.headers instanceof Headers) {
            headersToRemove.forEach(header => {
              init.headers.delete(header);
            });
          } else if (typeof init.headers === 'object') {
            headersToRemove.forEach(header => {
              delete init.headers[header];
            });
          }
          
          // Call original fetch directly without Application Insights tracking
          return await originalFetch(input, init);
        }
        
        if (isOwnBackend && !skipCorrelationHeaders && this.appInsights.core) {
          try {
            // Try to get correlation context - different approaches for different SDK versions
            let context = null;
              // Method 1: Try getCorrelationContext (older versions)
            if (typeof this.appInsights.core.getCorrelationContext === 'function') {
              console.debug('📊 Using getCorrelationContext method');
              context = this.appInsights.core.getCorrelationContext();
            }
            // Method 2: Try accessing context through telemetryTrace (newer versions)
            else if (this.appInsights.context && this.appInsights.context.telemetryTrace) {
              console.debug('📊 Using telemetryTrace method');
              context = {
                currentRequestId: this.appInsights.context.telemetryTrace.traceID,
                operation: {
                  traceparent: this.appInsights.context.telemetryTrace.traceparent
                }
              };
            }
            
            if (context) {
              if (context.currentRequestId) {
                init.headers['Request-Id'] = context.currentRequestId;
              }
              init.headers['Request-Context'] = 
                `appId=${this.appInsights.config.instrumentationKey}`;
              
              if (context.operation?.traceparent) {
                init.headers.traceparent = context.operation.traceparent;
              }
              console.debug('📊 Added correlation headers for request to:', url);
            } else {
              // Fallback: generate basic correlation headers
              const traceId = this.generateTraceId();
              init.headers['Request-Id'] = traceId;
              init.headers['Request-Context'] = 
                `appId=${this.appInsights.config.instrumentationKey}`;
              console.debug('📊 Added fallback correlation headers for request to:', url);
            }          } catch (correlationError) {
            console.warn('⚠️ Could not set up correlation headers:', correlationError.message);            // Continue without correlation headers
          }
        } else if (skipCorrelationHeaders) {
          console.log('🔍 Skipping correlation headers - details:', {
            url,
            isFileUpload,
            isMediaUpload,
            isSoundFontFile,
            isCorporateProxy,
            reason: isSoundFontFile ? 'SoundFont file detected' : 'Corporate proxy with file/media upload'
          });
        } else if (!isOwnBackend) {
          console.debug('🔍 Skipping correlation headers for external URL:', url);
        }// Track the request
        const startTime = Date.now();
        try {
          const response = await originalFetch(input, init);
          
          // Try different tracking methods based on what's available
          if (typeof this.appInsights.trackDependency === 'function') {
            this.appInsights.trackDependency({
              name: url || 'fetch',
              target: url || 'fetch',
              success: response.ok,
              duration: Date.now() - startTime,
              resultCode: response.status.toString(),
              properties: {
                method: init.method || 'GET',
                type: 'fetch'
              }
            });
          } else if (typeof this.appInsights.trackEvent === 'function') {
            // Fallback to event tracking
            this.appInsights.trackEvent({
              name: 'fetch_request',
              properties: {
                url: url || 'fetch',
                method: init.method || 'GET',
                success: response.ok,
                status: response.status,
                duration: Date.now() - startTime
              }
            });
          }
          
          return response;
        } catch (error) {
          // Try different tracking methods based on what's available
          if (typeof this.appInsights.trackDependency === 'function') {
            this.appInsights.trackDependency({
              name: url || 'fetch',
              target: url || 'fetch',
              success: false,
              duration: Date.now() - startTime,
              resultCode: '0',
              properties: {
                method: init.method || 'GET',
                error: error.message,
                type: 'fetch'
              }
            });
          } else if (typeof this.appInsights.trackEvent === 'function') {
            // Fallback to event tracking
            this.appInsights.trackEvent({
              name: 'fetch_request_error',
              properties: {
                url: url || 'fetch',
                method: init.method || 'GET',
                error: error.message,
                duration: Date.now() - startTime
              }
            });
          }
          
          throw error;
        }};
      console.log('✅ Application Insights fetch tracking setup completed successfully');
    } catch (error) {
      console.error('❌ Error setting up fetch tracking:', error);
    }
  }

  // Flush telemetry
  flush() {
    if (!this.initialized || !this.appInsights) return;
    try {
      this.appInsights.flush();
    } catch (e) {
      console.error('Error flushing telemetry:', e);
    }
  }

  // Add debug listeners
  _addDebugListeners() {
    if (!this.initialized || !this.appInsights) return;
    try {
      this.appInsights.addTelemetryInitializer((envelope) => {
        console.debug('📤 Telemetry:', {
          name: envelope.name,
          type: envelope.baseType,
          properties: envelope.data?.baseData?.properties
        });
        return true;
      });

      if (this.appInsights.core) {
        this.appInsights.core.addNotificationListener({
          eventsSent: (events) => {
            console.log('✅ Events sent:', events?.length || 0);
          },
          eventsDiscarded: (events, reason) => {
            console.warn('⚠️ Events discarded:', events?.length || 0, reason);
          },
          eventsSendRequest: (reason, isAsync) => {
            console.log('📡 Sending events:', reason, isAsync ? '(async)' : '(sync)');
          }
        });
      }
    } catch (error) {
      console.warn('Failed to add debug listeners:', error);
    }
  }

  // Helper method to generate a trace ID for correlation
  generateTraceId() {
    // Generate a 32-character hexadecimal string
    return Array.from({length: 32}, () => Math.floor(Math.random() * 16).toString(16)).join('');
  }
}

// Create a singleton instance
const appInsightsService = new AppInsightsService();
export default appInsightsService;
